import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { billingAPI, meetAPI } from '../../services/api';
import StripePaymentButton from '../../components/StripePaymentButton';
import './StudentPages.css';

const formatPhp = (amount) => new Intl.NumberFormat('en-PH', {
  style: 'currency',
  currency: 'PHP',
  maximumFractionDigits: 0
}).format(Number(amount || 0));

const Dashboard = () => {
  const { user } = useAuth();
  const [upcomingLessons, setUpcomingLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billingStatus, setBillingStatus] = useState(null);
  const [billingSummary, setBillingSummary] = useState(null);
  const [publishableKey, setPublishableKey] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const bookingsRes = await meetAPI.getBookings({ upcoming: 'true', status: 'confirmed' });
      setUpcomingLessons(bookingsRes.data.bookings.slice(0, 3));

      const statusRes = await billingAPI.getStatus();
      setBillingStatus(statusRes.data);

      const now = new Date();
      const month = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
      const summaryRes = await billingAPI.getMonthlySummary({ month });
      setBillingSummary(summaryRes.data);

      const keyRes = await billingAPI.getPublishableKey();
      setPublishableKey(keyRes.data.publishableKey || '');
    } catch (error) {
      console.error('Dashboard data error:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-PH', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex-center" style={{ height: '50vh' }}>
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div className="student-dashboard fade-in">
      <section className="welcome-section">
        <div className="welcome-content">
          <h1>Welcome, {user?.name}!</h1>
          <p className="welcome-subtitle">
            Ready to learn Japanese today?
          </p>
        </div>
      </section>

      <section className="book-lesson-cta">
        <div className="cta-content">
          <div className="cta-text">
            <h2>📅 Book Your Next Lesson</h2>
            <p>Schedule a lesson with our Japanese teachers</p>
          </div>
          <Link to="/student/book" className="btn btn-primary btn-lg">
            Book a Lesson
          </Link>
        </div>
      </section>

      <section className="dashboard-section">
        <div className="section-header">
          <h2>💳 Monthly Billing</h2>
          <Link to="/student/billing" className="view-all">Billing Details →</Link>
        </div>

        {!billingStatus?.stripeConfigured ? (
          <div className="empty-state">
            <p>Stripe is not configured yet.</p>
            <Link to="/student/billing" className="btn btn-primary">Open Billing</Link>
          </div>
        ) : !billingStatus?.hasPaymentMethod ? (
          <div className="empty-state">
            <p>Please save a card to enable Stripe payments.</p>
            <Link to="/student/billing" className="btn btn-primary">Setup Billing</Link>
          </div>
        ) : (
          <div className="lesson-card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontWeight: 600 }}>Current balance</div>
              <div className="text-muted">{billingSummary?.month} / {formatPhp(billingSummary?.totalAmount || 0)}</div>
              {billingStatus?.paymentMethodSummary && (
                <div className="text-muted" style={{ marginTop: 4 }}>
                  {String(billingStatus.paymentMethodSummary.brand || '').toUpperCase()} •••• {billingStatus.paymentMethodSummary.last4}
                </div>
              )}
            </div>

            <div style={{ minWidth: 220 }}>
              {billingSummary?.totalAmount > 0 ? (
                <StripePaymentButton
                  publishableKey={publishableKey}
                  month={billingSummary.month}
                  totalAmount={billingSummary.totalAmount}
                  onSuccess={fetchDashboardData}
                  compact
                />
              ) : (
                <button className="btn btn-secondary" disabled>No Due</button>
              )}
            </div>
          </div>
        )}
      </section>

      <section className="dashboard-section">
        <div className="section-header">
          <h2>📚 Upcoming Lessons</h2>
          <Link to="/student/bookings" className="view-all">View All →</Link>
        </div>
        <div className="upcoming-lessons">
          {upcomingLessons.length > 0 ? (
            upcomingLessons.map((booking) => (
              <div key={booking.id} className="lesson-card">
                <div className="lesson-time">
                  <span className="lesson-date">{formatDate(booking.scheduled_at)}</span>
                </div>
                <div className="lesson-info">
                  <h4>{booking.lesson_title || 'Japanese Lesson'}</h4>
                  <p>with {booking.teacher_name}</p>
                </div>
                {booking.google_meet_link && (
                  <a
                    href={booking.google_meet_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary btn-sm"
                  >
                    Join Meet
                  </a>
                )}
              </div>
            ))
          ) : (
            <div className="empty-state">
              <p>No upcoming lessons scheduled</p>
              <Link to="/student/book" className="btn btn-outline">
                Book Your First Lesson
              </Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Dashboard;
